import { DataTable } from "@/components/molecules/tables/data-table";
import { useAdminTenants, type TenantOverview } from "@/features/admin";
import { formatDuration } from "@/features/utils/numbers";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageLayout } from "../layout";
import { PeriodSelect } from "../analytics/components/period-select";

const columns: ColumnDef<TenantOverview>[] = [
  {
    accessorKey: "name",
    header: "Tenant",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.logo ? (
          <img
            src={row.original.logo}
            alt=""
            className="h-6 w-6 rounded object-cover"
          />
        ) : (
          <div className="h-6 w-6 rounded bg-muted flex items-center justify-center text-[10px] font-semibold text-muted-foreground">
            {row.original.name?.slice(0, 2).toUpperCase()}
          </div>
        )}
        {/*
          To the client's file here, not to `/<client>/home`: that link led
          into the client's own back-office, which platform staff cannot enter
          — every route behind it wants an ACTIVE membership they do not have.
          It looked like a way in and answered 403.
        */}
        <Link
          to={`/admin/clients/${row.original.id}`}
          className="font-medium hover:underline"
        >
          {row.original.name}
        </Link>
      </div>
    ),
  },
  {
    accessorKey: "minutes_30d",
    header: () => <div className="text-right">Minutes (30j)</div>,
    cell: ({ row }) => (
      <div className="text-right tabular-nums">
        {formatDuration(row.original.minutes_30d * 60)}
      </div>
    ),
  },
  {
    accessorKey: "calls_30d",
    header: () => <div className="text-right">Appels (30j)</div>,
    cell: ({ row }) => (
      <div className="text-right tabular-nums">{row.original.calls_30d}</div>
    ),
  },
  {
    accessorKey: "sessions_30d",
    header: () => <div className="text-right">Sessions (30j)</div>,
    cell: ({ row }) => (
      <div className="text-right tabular-nums">{row.original.sessions_30d}</div>
    ),
  },
  {
    accessorKey: "users",
    header: () => <div className="text-right">Utilisateurs</div>,
    cell: ({ row }) => (
      <div className="text-right tabular-nums">{row.original.users}</div>
    ),
  },
];

/**
 * Every client, with the indicators that say which one to open.
 *
 * The way into a client's file (`./detail.tsx`), which is the screen this list
 * exists to lead to — so the row is a link and the columns are what you would
 * scan before clicking one.
 */
export function AdminClientsPage() {
  const [days, setDays] = useState(30);
  const { data = [], isLoading } = useAdminTenants(days);

  return (
    <AdminPageLayout
      title="Clients"
      description="Ouvrir la fiche d'un client : ses postes, ses bases, ses accès, ses canaux."
      actions={[<PeriodSelect days={days} onChange={setDays} />]}
    >
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
      />
    </AdminPageLayout>
  );
}
